/**
 * @desc        Axios Interceptor
 * @version     2.0.1 (Added automation token required url) - 25 May 2026
 * */

import Axios from 'axios'
import * as Sentry from '@sentry/react'
import pLimit from 'p-limit'

const tokenRequiredUrls = [
    '/roles',
    '/user',
    '/paddocks',
    '/assets',
    '/sensortypes',
    '/sensor',
    '/payload',
    '/sensors',
    '/internal',
    '/settings',
    '/livestock',
    '/enumItems',
    '/me',
    '/farms',
    '/parameters',
    '/planningEvents',
    '/analytics',
    '/events',
    '/alertconfigs',
    '/alertconfig',
    '/automation',
]

Axios.defaults.timeout = 10000
const limit = pLimit(4) // Maximum number of concurrent requests

const reportApiError = error => {
    const { config, response } = error || {}

    if (response && response.status < 500) return

    Sentry.captureException(error, {
        tags: {
            source: 'axios-interceptor',
            httpStatus: response?.status || 'network-error',
        },
        extra: {
            method: config?.method?.toUpperCase(),
            url: config?.url?.split('?')[0],
        },
    })
}

export const configureInterceptors = () => {
    Axios.interceptors.request.use(
        request => {
            return limit(() => {
                // occassionally the tenant/farm is wrapped in quotes, this removes them
                if (request.url.includes('"')) {
                    request.url = request.url.replace(/"/g, '')
                }

                if (!request || !request.url || (request.url.includes('/*/') && request.method === 'get')) {
                    return Promise.reject({ isInvalidRequest: true, message: 'Invalid request' })
                }

                if (request.url.includes('/oauth/token')) {
                    request.headers = {
                        ...request.headers,
                        Accept: 'application/json',
                        'Content-Type': 'application/x-www-form-urlencoded',
                    }
                    return request
                } else if (tokenRequiredUrls.some(url => request.url.includes(url))) {
                    if (request.url.includes('null')) {
                        return Promise.reject({ isInvalidRequest: true, message: 'Invalid request' })
                    }
                    const cidlau = localStorage.getItem('cidlau')
                    const idToken = localStorage.getItem(`CognitoIdentityServiceProvider.${cidlau}.idToken`)
                    request.headers = {
                        ...request.headers,
                        Authorization: `Bearer ${idToken}`,
                        'Content-Type': 'application/json',
                    }
                    return request
                }
                return request
            })
        },
        error => {
            if (!error.isInvalidRequest) {
                // console.error(error)
            }
            return Promise.reject(error)
        },
    )

    Axios.interceptors.response.use(
        response => response,
        error => {
            const { config, response } = error || {}

            const isDemo403 = config?.url?.includes('/fmd-yerondavale/api/v1/') && response?.status === 403

            if (isDemo403) {
                // Prevent React/Webpack overlay by resolving silently
                return new Promise(resolve => resolve({ data: null, suppressed: true }))
            }

            // suppress `Invalid request` overlay too
            if (error?.isInvalidRequest) {
                return new Promise(resolve => resolve({ data: null, suppressed: true }))
            }

            reportApiError(error)
            return Promise.reject(error)
        },
    )
}

//TODO from Medium. using interceptor to auto refresh expired tokens. maybe future
//https://medium.com/@barisberkemalkoc/axios-interceptor-intelligent-db46653b7303
// axios.interceptors.response.use(
//     response => response,
//     async error => {
//       if (error.response.status === 401) {
//         const newToken = await refreshToken();
//         localStorage.setItem('authToken', newToken);
//         // Retry the original request
//         return axios(error.config);
//       }
//       return Promise.reject(error);
//     }
//   );
